import { Box, Link, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link as RouterLink, useInRouterContext } from 'react-router-dom';
import { QuotePdfDownload } from './QuotePdfDownload';
import { quotePdfDownloadPath } from './quotePdfContract';

interface AssistantMarkdownProps {
  content: string;
}

/**
 * Renders an assistant message as markdown using MUI typography.
 *
 * The assistant frequently emits bold, ordered and unordered lists, inline
 * code, and the occasional link — `<Typography>` alone leaves the asterisks
 * visible. We map only the elements the assistant actually produces so the
 * bubble stays compact (no oversized headings, no margin runaway).
 */
export function AssistantMarkdown({ content }: AssistantMarkdownProps) {
  const inRouter = useInRouterContext();
  return (
    <Box
      sx={{
        wordBreak: 'break-word',
        '& > :first-of-type': { mt: 0 },
        '& > :last-child': { mb: 0 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <Typography variant="body2" sx={{ my: 0.5 }}>
              {children}
            </Typography>
          ),
          strong: ({ children }) => (
            <Box component="strong" sx={{ fontWeight: 600 }}>
              {children}
            </Box>
          ),
          em: ({ children }) => (
            <Box component="em" sx={{ fontStyle: 'italic' }}>
              {children}
            </Box>
          ),
          ul: ({ children }) => (
            <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
              {children}
            </Box>
          ),
          ol: ({ children }) => (
            <Box component="ol" sx={{ my: 0.5, pl: 2.5 }}>
              {children}
            </Box>
          ),
          li: ({ children }) => (
            <Typography component="li" variant="body2" sx={{ my: 0.25 }}>
              {children}
            </Typography>
          ),
          h1: ({ children }) => (
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography variant="subtitle2" sx={{ mt: 0.75, mb: 0.5, fontWeight: 600 }}>
              {children}
            </Typography>
          ),
          code: ({ children }) => (
            <Box
              component="code"
              sx={(theme) => ({
                px: 0.5,
                py: 0.125,
                borderRadius: 0.5,
                fontFamily: 'monospace',
                fontSize: '0.85em',
                backgroundColor:
                  theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200],
              })}
            >
              {children}
            </Box>
          ),
          pre: ({ children }) => (
            <Box
              component="pre"
              sx={(theme) => ({
                my: 0.5,
                p: 1,
                borderRadius: 1,
                overflowX: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.85em',
                backgroundColor:
                  theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200],
              })}
            >
              {children}
            </Box>
          ),
          a: ({ href, title, children }) => {
            if (href && quotePdfDownloadPath(href)) {
              return <QuotePdfDownload href={href} fileName={title} />;
            }
            const internal =
              href?.startsWith('/') && !href.startsWith('//') && !href.includes('\\');
            if (href && internal && inRouter) {
              return (
                <Link component={RouterLink} to={href} underline="hover">
                  {children}
                </Link>
              );
            }
            return (
              <Link
                href={href}
                target={internal ? undefined : '_blank'}
                rel={internal ? undefined : 'noopener noreferrer'}
                underline="hover"
              >
                {children}
              </Link>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
}
